/**
 * @fileoverview Detail Cards
 * @description Card layout of a detail report: overview meta, summary highlights, and every
 * result table the report publishes, including the ones it collapses behind a disclosure.
 * @module features/ScoutEval/components/DetailCards
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ScoreChip } from './common';

/**
 * Render a parsed table, tinting any cell the report scored.
 * @param {Object} props - Table columns and rows.
 * @returns {JSX.Element} Result table.
 */
function ResultTable({ table }) {
  return (
    <TableContainer className="scout-detail-table-container">
      <Table size="small" className="scout-table">
        <TableHead>
          <TableRow>
            {table.columns.map((column) => <TableCell key={column}>{column}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {table.rows.map((row, rowIndex) => (
            // Report rows carry no stable identifier, so position is the only key available.
            <TableRow key={`row-${rowIndex}`} hover>
              {row.map((cell, cellIndex) => (
                <TableCell key={`cell-${cellIndex}`} className="scout-detail-cell">
                  {cell.band
                    ? <ScoreChip score={cell.score} band={cell.band} label={cell.value} />
                    : cell.value}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Render one report card.
 * @param {Object} props - Parsed card.
 * @returns {JSX.Element} Card.
 */
function DetailCard({ card }) {
  const [expanded, setExpanded] = useState(false);
  const isCollapsible = Boolean(card.collapsedLabel && card.table);

  return (
    <Paper className="scout-detail-card" elevation={0}>
      {card.heading && <Typography className="scout-detail-card-heading">{card.heading}</Typography>}
      {card.subhead && <Typography className="scout-detail-card-subhead">{card.subhead}</Typography>}

      {card.kpi && (
        <Box className="scout-detail-kpi">
          <Typography className="scout-metric-label">{card.kpi.label}</Typography>
          <Typography className="scout-detail-kpi-value">{card.kpi.value}</Typography>
        </Box>
      )}

      {card.meta.length > 0 && (
        <Box className="scout-detail-meta">
          {card.meta.map((entry) => (
            <React.Fragment key={entry.label}>
              <Typography className="scout-detail-meta-label">{entry.label}</Typography>
              <Typography className="scout-detail-meta-value">{entry.value}</Typography>
            </React.Fragment>
          ))}
        </Box>
      )}

      {card.highlights.length > 0 && (
        <Box className="scout-group-highlights">
          <ul>{card.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}</ul>
        </Box>
      )}

      {card.table && (isCollapsible ? (
        <>
          <Button
            size="small"
            className="scout-detail-disclosure"
            startIcon={expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {card.collapsedLabel}
          </Button>
          {expanded && <ResultTable table={card.table} />}
        </>
      ) : <ResultTable table={card.table} />)}

      {card.note && <Typography className="scout-detail-card-note">{card.note}</Typography>}
    </Paper>
  );
}

/**
 * Render every card of a detail report.
 * @param {Object} props - Parsed card layout.
 * @returns {JSX.Element} Card list.
 */
export function DetailCards({ content }) {
  return (
    <Box className="scout-detail-cards">
      {content.cards.map((card) => <DetailCard key={card.key} card={card} />)}
    </Box>
  );
}

export default DetailCards;
